/**
 * Слова оболочки на трёх языках (тикет T18b, спецификация § 11).
 *
 * Здесь только надписи самой оболочки: кнопки, заголовки, подсказки. Всё, что человек
 * получает от сервера, — названия Типов Блоков, объяснения бед, названия Каналов —
 * приходит уже переведённым и здесь не дублируется. Перевод одного и того же в двух
 * местах расходится ровно тогда, когда один из двух правят.
 *
 * Пропущенный язык не допускается по типу: `Record<Language, string>` требует все три.
 * Иначе иврит однажды тихо стал бы английским, и заметил бы это только тот, кто на нём
 * читает, — то есть заказчик.
 */
import type { Language } from '@core/settings/appearance.js';

export type Translated = Readonly<Record<Language, string>>;

export const DICTIONARY = {
  'app.name': {
    ru: 'Корпоративный мессенджер',
    en: 'Corporate messenger',
    he: 'מסנג׳ר תאגידי',
  },

  'common.save': { ru: 'Сохранить', en: 'Save', he: 'שמירה' },
  'common.cancel': { ru: 'Отменить', en: 'Cancel', he: 'ביטול' },
  'common.close': { ru: 'Закрыть', en: 'Close', he: 'סגירה' },
  'common.retry': { ru: 'Попробовать ещё раз', en: 'Try again', he: 'לנסות שוב' },
  'common.loading': { ru: 'Загружаем…', en: 'Loading…', he: 'טוען…' },
  'common.saving': { ru: 'Сохраняем…', en: 'Saving…', he: 'שומר…' },
  'common.saved': { ru: 'Сохранено', en: 'Saved', he: 'נשמר' },
  'common.empty': { ru: 'Пока пусто', en: 'Nothing here yet', he: 'עדיין ריק' },
  'common.back': { ru: 'Назад', en: 'Back', he: 'חזרה' },
  'common.yes': { ru: 'Да', en: 'Yes', he: 'כן' },
  'common.no': { ru: 'Нет', en: 'No', he: 'לא' },
  'common.showPassword': { ru: 'Показать пароль', en: 'Show password', he: 'הצגת הסיסמה' },
  'common.hidePassword': { ru: 'Скрыть пароль', en: 'Hide password', he: 'הסתרת הסיסמה' },
  'common.offline': {
    ru: 'Нет связи с сервером — проверьте подключение',
    en: 'No connection to the server — check your network',
    he: 'אין קשר לשרת — בדקו את החיבור',
  },
  'common.serverDown': {
    ru: 'Сервер сейчас не отвечает — попробуйте через минуту',
    en: 'The server is not responding — try again in a minute',
    he: 'השרת אינו מגיב — נסו שוב בעוד דקה',
  },
  'common.badAnswer': {
    ru: 'Сервер ответил в непонятном виде',
    en: 'The server sent an answer we could not read',
    he: 'השרת שלח תשובה שלא הצלחנו לקרוא',
  },
  'common.serverRefused': {
    ru: 'Сервер не принял запрос',
    en: 'The server refused the request',
    he: 'השרת דחה את הבקשה',
  },

  'skip.toMain': {
    ru: 'Сразу к содержимому',
    en: 'Skip to main content',
    he: 'דילוג לתוכן הראשי',
  },

  'signIn.title': { ru: 'Вход', en: 'Sign in', he: 'כניסה' },
  'signIn.space': { ru: 'Пространство фирмы', en: 'Company space', he: 'מרחב החברה' },
  'signIn.spaceHint': {
    ru: 'Короткое имя фирмы в адресе — его выдал Администратор',
    en: 'The short company name from the address — your administrator gave it to you',
    he: 'השם הקצר של החברה בכתובת — קיבלתם אותו מהמנהל',
  },
  'signIn.email': { ru: 'Почта', en: 'Email', he: 'דוא״ל' },
  'signIn.password': { ru: 'Пароль', en: 'Password', he: 'סיסמה' },
  'signIn.submit': { ru: 'Войти', en: 'Sign in', he: 'כניסה' },
  'signIn.working': { ru: 'Входим…', en: 'Signing in…', he: 'נכנסים…' },
  'signIn.needAll': {
    ru: 'Заполните Пространство, почту и пароль',
    en: 'Fill in the space, email and password',
    he: 'מלאו מרחב, דוא״ל וסיסמה',
  },
  'signIn.failed': {
    ru: 'Войти не получилось — проверьте Пространство, почту и пароль',
    en: 'Sign-in failed — check the space, email and password',
    he: 'הכניסה נכשלה — בדקו מרחב, דוא״ל וסיסמה',
  },

  'header.settings': { ru: 'Настройки', en: 'Settings', he: 'הגדרות' },
  'header.signOut': { ru: 'Выйти', en: 'Sign out', he: 'יציאה' },
  'header.menu': { ru: 'Меню', en: 'Menu', he: 'תפריט' },
  'header.workspace': { ru: 'Рабочее место', en: 'Workspace', he: 'סביבת עבודה' },

  'presence.online': { ru: 'В сети', en: 'Online', he: 'מחובר' },
  'presence.away': { ru: 'Отошёл', en: 'Away', he: 'מרוחק' },
  'presence.dnd': { ru: 'Не беспокоить', en: 'Do not disturb', he: 'נא לא להפריע' },
  'presence.invisible': { ru: 'Невидимка', en: 'Invisible', he: 'בלתי נראה' },
  'presence.label': { ru: 'Моё присутствие', en: 'My presence', he: 'הנוכחות שלי' },

  'channels.title': { ru: 'Каналы', en: 'Channels', he: 'ערוצים' },
  'channels.empty': {
    ru: 'Каналов пока нет — их заводит Администратор',
    en: 'No channels yet — your administrator creates them',
    he: 'אין עדיין ערוצים — המנהל יוצר אותם',
  },
  'channels.failed': {
    ru: 'Не удалось получить список Каналов',
    en: 'Could not load the list of channels',
    he: 'לא הצלחנו לטעון את רשימת הערוצים',
  },
  'channels.current': { ru: 'Открытый Канал', en: 'Current channel', he: 'הערוץ הפתוח' },
  'channels.create': { ru: 'Новый Канал', en: 'New channel', he: 'ערוץ חדש' },
  'channels.createTitle': { ru: 'Завести Канал', en: 'Create a channel', he: 'יצירת ערוץ' },
  'channels.name': { ru: 'Название Канала', en: 'Channel name', he: 'שם הערוץ' },
  'channels.kindPrivate': {
    ru: 'Закрытый — только для тех, кого добавили',
    en: 'Private — only for people who were added',
    he: 'סגור — רק למי שנוסף',
  },
  'channels.needName': {
    ru: 'Впишите название Канала',
    en: 'Enter a channel name',
    he: 'הזינו שם לערוץ',
  },
  'channels.created': { ru: 'Канал заведён', en: 'Channel created', he: 'הערוץ נוצר' },
  'channels.submit': { ru: 'Завести', en: 'Create', he: 'ליצור' },
  'channels.noBlock': {
    ru: 'Канал заведён, но место для переписки в нём не встало — соберите Раскладку',
    en: 'The channel was created, but the conversation block was not placed — arrange the layout',
    he: 'הערוץ נוצר, אך בלוק ההתכתבות לא הוצב — סדרו את הפריסה',
  },

  'admin.title': { ru: 'Управление', en: 'Administration', he: 'ניהול' },
  'admin.close': { ru: 'Свернуть', en: 'Hide', he: 'סגירה' },

  'invite.open': { ru: 'Пригласить работника', en: 'Invite a person', he: 'הזמנת עובד' },
  'invite.title': { ru: 'Приглашение в фирму', en: 'Invitation to the company', he: 'הזמנה לחברה' },
  'invite.email': { ru: 'Почта человека', en: 'Their email', he: 'הדוא״ל של האדם' },
  'invite.role': { ru: 'Роль', en: 'Role', he: 'תפקיד' },
  'invite.role.staff': { ru: 'Сотрудник', en: 'Staff', he: 'עובד' },
  'invite.role.head': { ru: 'Руководитель', en: 'Department head', he: 'ראש מחלקה' },
  'invite.role.guest': { ru: 'Гость', en: 'Guest', he: 'אורח' },
  'invite.role.admin': { ru: 'Администратор', en: 'Administrator', he: 'מנהל' },
  'invite.submit': { ru: 'Выпустить ссылку', en: 'Create a link', he: 'הפקת קישור' },
  'invite.working': { ru: 'Выпускаем…', en: 'Creating…', he: 'מפיקים…' },
  'invite.needEmail': { ru: 'Впишите почту', en: 'Enter an email', he: 'הזינו דוא״ל' },
  'invite.linkLabel': { ru: 'Ссылка-приглашение', en: 'Invitation link', he: 'קישור ההזמנה' },
  'invite.ready': {
    // Срок ссылки называем прямо: человек, отложивший письмо на две недели, иначе решит,
    // что сломалось приложение, а не что Приглашение погасло по времени.
    ru: 'Ссылка готова — отправьте её человеку. Она действует 7 дней и срабатывает один раз.',
    en: 'The link is ready — send it to them. It works for 7 days and only once.',
    he: 'הקישור מוכן — שלחו אותו לאדם. הוא תקף שבעה ימים ופועל פעם אחת.',
  },
  'invite.copy': { ru: 'Скопировать ссылку', en: 'Copy link', he: 'העתקת הקישור' },
  'invite.copied': { ru: 'Ссылка скопирована', en: 'Link copied', he: 'הקישור הועתק' },

  'join.title': { ru: 'Присоединиться к фирме', en: 'Join the company', he: 'הצטרפות לחברה' },
  'join.intro': {
    ru: 'Вас позвали в Пространство «{space}». Задайте себе имя и пароль — почта уже известна.',
    en: 'You were invited to the “{space}” space. Choose your name and password — your email is already known.',
    he: 'הוזמנתם למרחב ״{space}״. בחרו שם וסיסמה — הדוא״ל כבר ידוע.',
  },
  'join.name': { ru: 'Как вас показывать', en: 'How to show your name', he: 'איך להציג אתכם' },
  'join.password': { ru: 'Придумайте пароль', en: 'Choose a password', he: 'בחרו סיסמה' },
  'join.passwordHint': {
    ru: 'Не короче 10 знаков',
    en: 'At least 10 characters',
    he: 'לפחות 10 תווים',
  },
  // Подсказка под полем и отказ после нажатия — разные слова: подсказка говорит, каким
  // пароль должен быть, отказ — что с присланным не так.
  'join.passwordShort': {
    ru: 'Пароль слишком короткий — нужно не меньше 10 знаков',
    en: 'That password is too short — at least 10 characters are needed',
    he: 'הסיסמה קצרה מדי — נדרשים לפחות 10 תווים',
  },
  'join.submit': { ru: 'Присоединиться', en: 'Join', he: 'הצטרפות' },
  'join.working': { ru: 'Заводим Учётную запись…', en: 'Creating your account…', he: 'יוצרים חשבון…' },
  'join.needAll': { ru: 'Впишите имя и пароль', en: 'Enter a name and a password', he: 'הזינו שם וסיסמה' },
  'join.failed': {
    ru: 'Присоединиться не получилось — проверьте ссылку, возможно, ею уже воспользовались',
    en: 'Joining failed — check the link, it may have been used already',
    he: 'ההצטרפות נכשלה — בדקו את הקישור, ייתכן שכבר השתמשו בו',
  },
  'join.toSignIn': { ru: 'Уже присоединялись? Войдите', en: 'Already joined? Sign in', he: 'כבר הצטרפתם? היכנסו' },

  'layout.title': { ru: 'Раскладка', en: 'Layout', he: 'פריסה' },
  'layout.empty': {
    ru: 'На этом Канале ещё нет ни одного Блока',
    en: 'This channel has no blocks yet',
    he: 'בערוץ הזה עדיין אין אף בלוק' ,
  },
  'layout.emptyAdmin': {
    ru: 'Добавьте первый Блок — кнопка выше',
    en: 'Add the first block with the button above',
    he: 'הוסיפו בלוק ראשון בעזרת הכפתור למעלה',
  },
  'layout.addBlock': { ru: 'Добавить Блок', en: 'Add block', he: 'הוספת בלוק' },
  'layout.addChoose': { ru: 'Какой Блок добавить', en: 'Which block to add', he: 'איזה בלוק להוסיף' },
  'layout.addHint': {
    ru: 'Нажмите на нужный — Блок сразу появится в Канале. Потом его можно передвинуть, свернуть или убрать.',
    en: 'Pick one — the block appears in the channel right away. You can move, collapse or remove it later.',
    he: 'בחרו אחד — הבלוק יופיע בערוץ מיד. אחר כך אפשר להזיז, לכווץ או להסיר אותו.',
  },
  'layout.addWhere': {
    ru: 'Блок встанет на экран Канала «{where}»',
    en: 'The block will be placed on the “{where}” channel screen',
    he: 'הבלוק ימוקם במסך הערוץ „{where}”',
  },
  'layout.notReadyTag': {
    ru: 'на сайте пока не показывается',
    en: 'not shown on the web yet',
    he: 'עדיין לא מוצג באתר',
  },
  'layout.added': {
    ru: 'Блок добавлен: {title}. Столбец {col}, строка {row}',
    en: 'Block added: {title}. Column {col}, row {row}',
    he: 'נוסף בלוק: {title}. עמודה {col}, שורה {row}',
  },
  'layout.addedHere': { ru: 'добавлен', en: 'added', he: 'נוסף' },
  'layout.removeBlock': { ru: 'Убрать Блок', en: 'Remove block', he: 'הסרת בלוק' },
  'layout.removeConfirm': {
    ru: 'Убрать этот Блок с Раскладки?',
    en: 'Remove this block from the layout?',
    he: 'להסיר את הבלוק הזה מהפריסה?',
  },
  'layout.editOn': { ru: 'Собрать Раскладку', en: 'Arrange layout', he: 'עריכת הפריסה' },
  'layout.editOff': { ru: 'Закончить сборку', en: 'Finish arranging', he: 'סיום העריכה' },
  'layout.onlyWide': {
    ru: 'На узком экране Блоки идут столбиком. Собрать Раскладку можно с большого экрана.',
    en: 'On a narrow screen blocks stack in one column. Arrange the layout on a large screen.',
    he: 'במסך צר הבלוקים מסודרים בטור אחד. לעריכת הפריסה יש להשתמש במסך גדול.',
  },
  'layout.personalOn': { ru: 'Настроить под себя', en: 'Arrange for me', he: 'סידור אישי' },
  'layout.personalOff': {
    ru: 'Закончить настройку',
    en: 'Finish my arrangement',
    he: 'סיום הסידור האישי',
  },
  'layout.personalForget': { ru: 'Вернуть как у всех', en: 'Reset to shared', he: 'חזרה לפריסה המשותפת' },
  'layout.personalHint': {
    ru: 'Вы двигаете Блоки только у себя — остальные видят Канал прежним. Набор Блоков общий и здесь не меняется.',
    en: 'You are moving blocks only on your own screen — others still see the channel as it was. The set of blocks is shared and does not change here.',
    he: 'אתם מזיזים בלוקים רק אצלכם — האחרים ממשיכים לראות את הערוץ כפי שהיה. מערך הבלוקים משותף ואינו משתנה כאן.',
  },
  'layout.personalReset': {
    ru: 'Раскладка снова как у всех',
    en: 'Layout is shared again',
    he: 'הפריסה שוב משותפת',
  },
  'layout.blockMenu': { ru: 'Что сделать с Блоком', en: 'Block actions', he: 'פעולות על הבלוק' },
  'layout.move': { ru: 'Передвинуть', en: 'Move', he: 'הזזה' },
  'layout.resize': { ru: 'Изменить размер', en: 'Resize', he: 'שינוי גודל' },
  // Подпись уголка в дальнем нижнем углу карточки: мышь узнаёт его назначение только так.
  'layout.resizeHandle': {
    ru: 'Потяните, чтобы изменить размер Блока',
    en: 'Drag to resize the block',
    he: 'גררו כדי לשנות את גודל הבלוק',
  },
  // Что делать в режиме сборки. Ручка и уголок видны только тому, кто уже знает про них.
  'layout.arrangeHint': {
    ru: 'Блок двигают за его название, а растягивают за уголок в правом нижнем углу. То же самое есть в меню Блока: «Перенести» и «Изменить размер».',
    en: 'Drag a block by its title to move it, or by the corner handle at the bottom right to resize it. The block menu offers the same: “Move” and “Resize”.',
    he: 'גוררים בלוק בעזרת הכותרת שלו כדי להזיז, ובעזרת הפינה הימנית התחתונה כדי לשנות גודל. אותו דבר קיים בתפריט הבלוק: «הזזה» ו«שינוי גודל».',
  },
  'layout.keyboardHint': {
    ru: 'Стрелки двигают Блок, стрелки с Shift меняют размер, Enter отпускает',
    en: 'Arrows move the block, Shift with arrows resizes it, Enter releases it',
    he: 'החצים מזיזים את הבלוק, Shift עם חצים משנה גודל, Enter משחרר',
  },
  'layout.grabbed': {
    ru: 'Блок взят. Стрелки двигают, Shift со стрелками меняет размер, Enter отпускает, Esc возвращает.',
    en: 'Block grabbed. Arrows move, Shift with arrows resizes, Enter releases, Esc puts it back.',
    he: 'הבלוק נתפס. חצים מזיזים, Shift עם חצים משנה גודל, Enter משחרר, Esc מחזיר.',
  },
  // Взяли Блок на размер: стрелки сразу растягивают, Shift на это время двигает.
  'layout.grabbedSize': {
    ru: 'Блок взят на размер. Стрелки меняют ширину и высоту, Shift со стрелками двигает, Enter отпускает, Esc возвращает.',
    en: 'Block grabbed for resizing. Arrows change width and height, Shift with arrows moves it, Enter releases, Esc puts it back.',
    he: 'הבלוק נתפס לשינוי גודל. חצים משנים רוחב וגובה, Shift עם חצים מזיז, Enter משחרר, Esc מחזיר.',
  },
  'layout.released': { ru: 'Блок отпущен', en: 'Block released', he: 'הבלוק שוחרר' },
  'layout.position': { ru: 'Место', en: 'Position', he: 'מיקום' },
  'layout.spoken': {
    // Голос для диктора. Знаков вроде «×» и «·» здесь нарочно нет: разные дикторы читают
    // их по-разному, а иные молчат вовсе, и человек услышал бы «два четыре» без смысла.
    ru: 'Столбец {col}, строка {row}, ширина {width}, высота {height}',
    en: 'Column {col}, row {row}, width {width}, height {height}',
    he: 'עמודה {col}, שורה {row}, רוחב {width}, גובה {height}',
  },
  'layout.guestVisible': { ru: 'Видно Гостю', en: 'Visible to guests', he: 'גלוי לאורחים' },

  'block.failed': { ru: 'Блок не показывается', en: 'This block cannot be shown', he: 'הבלוק אינו מוצג' },
  'block.details': { ru: 'Подробности', en: 'Details', he: 'פרטים' },
  'block.notReady': {
    ru: 'Этот Блок ещё не показывается на сайте',
    en: 'This block is not shown on the web yet',
    he: 'הבלוק הזה עדיין אינו מוצג באתר',
  },

  // Меню Блока — «три точки» в углу карточки (правка 2026-08-30).
  'block.collapse': { ru: 'Свернуть', en: 'Collapse', he: 'כיווץ' },
  'block.expand': { ru: 'Развернуть', en: 'Expand', he: 'הרחבה' },
  'block.hide': { ru: 'Скрыть', en: 'Hide', he: 'הסתרה' },
  'block.carry': { ru: 'Перенести', en: 'Move', he: 'הזזה' },
  'block.pin': { ru: 'Зафиксировать', en: 'Pin', he: 'קיבוע' },
  'block.unpin': { ru: 'Снять фиксацию', en: 'Unpin', he: 'ביטול קיבוע' },
  'block.pinned': { ru: 'Блок зафиксирован', en: 'Block is pinned', he: 'הבלוק מקובע' },
  'block.pinnedSpoken': {
    ru: 'Блок зафиксирован — снимите фиксацию в его меню, чтобы двигать',
    en: 'The block is pinned — unpin it in its menu to move it',
    he: 'הבלוק מקובע — בטלו את הקיבוע בתפריט שלו כדי להזיז אותו',
  },
  'block.settings': { ru: 'Настройки', en: 'Settings', he: 'הגדרות' },
  'block.background': { ru: 'Цвет фона', en: 'Background colour', he: 'צבע רקע' },
  'block.foreground': { ru: 'Цвет текста', en: 'Text colour', he: 'צבע טקסט' },
  'block.textScale': { ru: 'Размер букв', en: 'Text size', he: 'גודל הטקסט' },
  'block.colourShared': { ru: 'Как у всех', en: 'Shared', he: 'כמו לכולם' },
  'block.resetColours': { ru: 'Вернуть общий вид', en: 'Reset appearance', he: 'איפוס המראה' },
  'block.weakContrast': {
    ru: 'Буквы плохо видны на этом фоне — выберите цвета потемнее или посветлее',
    en: 'The text is hard to read on this background — pick darker or lighter colours',
    he: 'הטקסט קשה לקריאה על הרקע הזה — בחרו צבעים כהים או בהירים יותר',
  },
  'block.scale.small': { ru: 'Мельче', en: 'Smaller', he: 'קטן יותר' },
  'block.scale.normal': { ru: 'Обычный', en: 'Normal', he: 'רגיל' },
  'block.scale.large': { ru: 'Крупнее', en: 'Larger', he: 'גדול יותר' },
  'block.scale.huge': { ru: 'Очень крупный', en: 'Largest', he: 'הגדול ביותר' },
  'block.hiddenList': {
    ru: 'Скрытые Блоки: {count}',
    en: 'Hidden blocks: {count}',
    he: 'בלוקים מוסתרים: {count}',
  },
  'block.show': { ru: 'Вернуть: {title}', en: 'Show: {title}', he: 'החזרה: {title}' },
  'block.hiddenSpoken': {
    ru: 'Блок скрыт: {title}. Вернуть его можно списком над Раскладкой',
    en: 'Block hidden: {title}. You can bring it back from the list above the layout',
    he: 'הבלוק הוסתר: {title}. אפשר להחזיר אותו מהרשימה מעל הפריסה',
  },
  'block.shownSpoken': {
    ru: 'Блок снова на экране: {title}',
    en: 'Block is back on screen: {title}',
    he: 'הבלוק חזר למסך: {title}',
  },

  // Названия красок. Цвет без названия диктору не объяснить, а выбирают его и те, кто
  // цвета не различает.
  'colour.white': { ru: 'Белый', en: 'White', he: 'לבן' },
  'colour.lightGrey': { ru: 'Светло-серый', en: 'Light grey', he: 'אפור בהיר' },
  'colour.sand': { ru: 'Песочный', en: 'Sand', he: 'חולי' },
  'colour.mint': { ru: 'Мятный', en: 'Mint', he: 'מנטה' },
  'colour.sky': { ru: 'Голубой', en: 'Sky blue', he: 'תכלת' },
  'colour.lilac': { ru: 'Сиреневый', en: 'Lilac', he: 'לילך' },
  'colour.rose': { ru: 'Розовый', en: 'Rose', he: 'ורוד' },
  'colour.almostBlack': { ru: 'Почти чёрный', en: 'Almost black', he: 'כמעט שחור' },
  'colour.darkGrey': { ru: 'Тёмно-серый', en: 'Dark grey', he: 'אפור כהה' },
  'colour.darkBlue': { ru: 'Тёмно-синий', en: 'Dark blue', he: 'כחול כהה' },
  'colour.darkGreen': { ru: 'Тёмно-зелёный', en: 'Dark green', he: 'ירוק כהה' },
  'colour.darkRed': { ru: 'Тёмно-бордовый', en: 'Dark red', he: 'בורדו' },

  'messages.title': { ru: 'Переписка', en: 'Conversation', he: 'התכתבות' },
  'messages.write': { ru: 'Написать Сообщение', en: 'Write a message', he: 'כתיבת הודעה' },
  'messages.send': { ru: 'Отправить', en: 'Send', he: 'שליחה' },
  'messages.empty': {
    ru: 'Сообщений пока нет — напишите первое',
    en: 'No messages yet — write the first one',
    he: 'אין עדיין הודעות — כתבו את הראשונה',
  },
  'messages.read': { ru: 'Прочитано', en: 'Read', he: 'נקרא' },
  'messages.failed': {
    ru: 'Не удалось получить Сообщения',
    en: 'Could not load messages',
    he: 'לא הצלחנו לטעון הודעות',
  },
  'messages.edited': { ru: 'изменено', en: 'edited', he: 'נערך' },
  'messages.edit': { ru: 'Изменить', en: 'Edit', he: 'עריכה' },
  'messages.editSave': { ru: 'Сохранить', en: 'Save', he: 'שמירה' },
  'messages.editCancel': { ru: 'Не менять', en: 'Cancel', he: 'ביטול' },
  'messages.editing': {
    ru: 'Изменение Сообщения. Enter — сохранить, Escape — оставить как было',
    en: 'Editing a message. Enter saves, Escape leaves it as it was',
    he: 'עריכת הודעה. Enter לשמירה, Escape להשארה כפי שהייתה',
  },
  'messages.editConflict': {
    ru: 'Это Сообщение успели изменить с другого устройства. Мы показали свежий текст — посмотрите и внесите правку заново',
    en: 'This message was changed on another device. We have shown the newer text — please look and make your change again',
    he: 'ההודעה שונתה במכשיר אחר. הצגנו את הנוסח העדכני — עיינו ובצעו את השינוי מחדש',
  },
  'messages.remove': { ru: 'Убрать Сообщение', en: 'Remove message', he: 'הסרת הודעה' },
  'messages.removeConfirm': {
    ru: 'Убрать это Сообщение? На его месте останется пометка, что оно удалено.',
    en: 'Remove this message? A note that it was deleted will stay in its place.',
    he: 'להסיר את ההודעה? במקומה תישאר הערה שהיא נמחקה.',
  },
  'messages.removed': { ru: 'Сообщение удалено', en: 'Message deleted', he: 'ההודעה נמחקה' },
  'messages.unread': {
    ru: 'Непрочитанных Сообщений: {count}',
    en: 'Unread messages: {count}',
    he: 'הודעות שלא נקראו: {count}',
  },

  'tasks.title': { ru: 'Задачи', en: 'Tasks', he: 'משימות' },
  'tasks.empty': { ru: 'Задач нет', en: 'No tasks', he: 'אין משימות' },
  'tasks.assigned': { ru: 'Назначена', en: 'Assigned', he: 'הוקצתה' },
  'tasks.delivered': { ru: 'Сдана', en: 'Delivered', he: 'הוגשה' },
  'tasks.accepted': { ru: 'Принята', en: 'Accepted', he: 'התקבלה' },
  'tasks.returned': { ru: 'Возвращена', en: 'Returned', he: 'הוחזרה' },
  'tasks.due': { ru: 'Срок', en: 'Due', he: 'מועד' },
  'tasks.overdue': { ru: 'Срок вышел', en: 'Overdue', he: 'עבר המועד' },
  'tasks.failed': {
    ru: 'Не удалось получить Задачи',
    en: 'Could not load tasks',
    he: 'לא הצלחנו לטעון משימות',
  },

  'settings.title': { ru: 'Настройки', en: 'Settings', he: 'הגדרות' },
  'settings.mineOnly': {
    ru: 'Эти настройки — только ваши. Их не видит никто, включая Администратора.',
    en: 'These settings are yours alone. Nobody sees them, not even the administrator.',
    he: 'ההגדרות האלה שלכם בלבד. אף אחד לא רואה אותן, גם לא המנהל.',
  },
  'settings.groups': { ru: 'Разделы Настроек', en: 'Settings sections', he: 'מדורי ההגדרות' },
  'settings.group.appearance': { ru: 'Вид', en: 'Appearance', he: 'מראה' },
  'settings.group.language': { ru: 'Язык', en: 'Language', he: 'שפה' },
  'settings.group.accessibility': { ru: 'Доступность', en: 'Accessibility', he: 'נגישות' },
  'settings.group.notifications': { ru: 'Уведомления', en: 'Notifications', he: 'התראות' },
  'settings.group.sound': { ru: 'Звук', en: 'Sound', he: 'צליל' },
  'settings.group.messaging': { ru: 'Переписка', en: 'Messaging', he: 'התכתבות' },
  'settings.group.presence': { ru: 'Присутствие', en: 'Presence', he: 'נוכחות' },
  'settings.failed': {
    ru: 'Не удалось получить Настройки',
    en: 'Could not load settings',
    he: 'לא הצלחנו לטעון הגדרות',
  },
  'settings.reset': { ru: 'Вернуть как было у фирмы', en: 'Restore company default', he: 'החזרת ברירת המחדל של החברה' },
  'settings.channels': { ru: 'Отметки на Каналы', en: 'Per-channel notifications', he: 'סימוני ערוצים' },
  'settings.channelMode.default': { ru: 'Как у всех', en: 'Same as everywhere', he: 'כמו בכל מקום' },
  'settings.channelMode.mentions': { ru: 'Только упоминания', en: 'Mentions only', he: 'אזכורים בלבד' },
  'settings.channelMode.muted': { ru: 'Без уведомлений', en: 'Muted', he: 'ללא התראות' },
} as const satisfies Record<string, Translated>;

export type PhraseKey = keyof typeof DICTIONARY;

/**
 * Перевести. Подстановки — в фигурных скобках: `{name}`. Незнакомый ключ до сюда не
 * доходит — его не пропускает тип, поэтому «ключа нет» здесь не бывает.
 */
export const translate = (
  language: Language,
  key: PhraseKey,
  values?: Readonly<Record<string, string | number>>,
): string => {
  const phrase = DICTIONARY[key][language];
  if (!values) return phrase;
  return phrase.replace(/\{(\w+)\}/g, (whole, name: string) => {
    const value = values[name];
    return value === undefined ? whole : String(value);
  });
};

/** Готовый переводчик на один язык — его и раздают по экранам. */
export type Translator = (key: PhraseKey, values?: Readonly<Record<string, string | number>>) => string;

export const translatorFor =
  (language: Language): Translator =>
  (key, values) =>
    translate(language, key, values);
